import React from "react";
import PropTypes from "prop-types";
// Material imports
import makeStyles from "@mui/styles/makeStyles";
import Grid from "@mui/material/Grid";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import DialogTitle from "@mui/material/DialogTitle";
import ArrowBack from "@mui/icons-material/ArrowBack";

//Limitless imports
import { ONBOARD_TERMS_RESOURCES, CODE_OF_CONDUCT, EXPERT_TERMS, PRIVACY_POLICY, fetchPlatformResources } from "@limitless/common/apis/resource-api";
import { log as pilog } from "@limitless/common/utils";
import { useTranslation } from "@limitless/common/hooks";
import { RemoteContent, LoadingSpinner, PortalButton, PrivacyInfo } from "../..";
const useStyles = makeStyles(theme => ({
  dialog: {
    width: 600,
    borderRadius: 20,
    height: "90vh"
  },
  closeButton: {
    position: "absolute",
    zIndex: 999999,
    right: 10,
    marginBottom: 20,
    color: "white"
  },
  backButton: {
    position: "absolute",
    zIndex: 999999,
    left: 10,
    marginBottom: 20,
    color: "white"
  },
  dialogBackground: {
    backgroundColor: theme.palette.primary.dark,
    maxHeight: 10,
    padding: 0
  },
  loadingContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "center",
    alignItems: "center"
  },
  tabsFlexContainer: {
    justifyContent: "center",
    backgroundColor: "#34467a"
  },
  agreeTermsButton: {
    backgroundColor: theme.palette.primary.light,
    fontSize: ".7rem",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark
    }
  },
  tab: {
    opacity: "1",
    fontSize: 14,
    textTransform: "none",
    "&.Mui-selected": {
      background: theme.palette.primary.dark
    }
  },
  privacyNoteLink: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    cursor: "pointer"
  },
  buttonContainer: {
    display: "flex"
  },
  [theme.breakpoints.up("sm")]: {
    tab: {
      fontSize: 14
    },
    agreeTermsButton: {
      fontSize: ".8rem"
    }
  }
}));
const TermsDialog = ({
  open,
  onClose,
  acceptTerms,
  locale,
  showOnlyPrivacy = false,
  backToTerms = false,
  readOnly = false
}) => {
  const {
    t
  } = useTranslation();
  const [value, setValue] = React.useState(0);
  const [termsHTML, setTermsHTML] = React.useState(t("Terms Content not found"));
  const [privacyHTML, setPrivacyHTML] = React.useState(t("Privacy Content not found"));
  const [codeOfConductHTML, setcodeOfConductHTML] = React.useState(t("Code of Conduct not found"));
  const [privacyTabOnly, setPrivacyTabOnly] = React.useState(showOnlyPrivacy);
  const [loading, setLoading] = React.useState(true);
  const [termsLocale, setTermsLocale] = React.useState(0);
  const log = pilog.getLogger("TermsDialog");
  const classes = useStyles();
  const tabClasses = {
    root: classes.tab
  };
  React.useEffect(() => {
    if (open === true) {
      setPrivacyTabOnly(showOnlyPrivacy);
    }
  }, [open]);
  React.useEffect(() => {
    if (termsLocale) {
      loadResources(termsLocale);
    }
  }, []);
  React.useEffect(() => {
    if (locale !== termsLocale) {
      loadResources(locale);
    }
    setTermsLocale(locale);
  }, [locale]);
  const loadResources = locale => {
    fetchPlatformResources({
      locale,
      resourceList: ONBOARD_TERMS_RESOURCES,
      applicationName: "expert_web_app"
    }, processTermsContent, processTermsContent);
  };
  const processTermsContent = platformContent => {
    platformContent.map(resource => {
      switch (resource.resourceName) {
        case EXPERT_TERMS.name:
          if (resource.content) {
            setTermsHTML(resource.content);
          }
          break;
        case PRIVACY_POLICY.name:
          if (resource.content) {
            setPrivacyHTML(resource.content);
          }
          break;
        case CODE_OF_CONDUCT.name:
          if (resource.content) {
            setcodeOfConductHTML(resource.content);
          }
          break;
        default:
          log.warn(`Unexpected terms resource type ${resource.resourceName}`);
      }
    });
    setLoading(false);
  };
  const handleChange = (event, value) => {
    setValue(value);
  };
  const renderTerms = () => {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 40
      }
    }, /*#__PURE__*/React.createElement(RemoteContent, {
      content: termsHTML,
      wrapWhiteSpace: false
    }));
  };
  const renderPrivacy = () => {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 40
      }
    }, /*#__PURE__*/React.createElement(RemoteContent, {
      content: privacyHTML,
      wrapWhiteSpace: false
    }));
  };
  const renderCodeOfConduct = () => {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 40
      }
    }, /*#__PURE__*/React.createElement(RemoteContent, {
      content: codeOfConductHTML,
      wrapWhiteSpace: false
    }));
  };
  return /*#__PURE__*/React.createElement(Dialog, {
    open: open,
    onClose: onClose,
    classes: {
      paper: classes.dialog
    },
    disablePortal: false,
    maxWidth: "lg",
    fullWidth: true
  }, privacyTabOnly && /*#__PURE__*/React.createElement(DialogTitle, {
    className: classes.dialogBackground
  }, backToTerms && /*#__PURE__*/React.createElement(IconButton, {
    onClick: () => setPrivacyTabOnly(false),
    className: classes.backButton,
    size: "large"
  }, /*#__PURE__*/React.createElement(ArrowBack, null)), /*#__PURE__*/React.createElement(IconButton, {
    onClick: onClose,
    className: classes.closeButton,
    size: "large"
  }, /*#__PURE__*/React.createElement(CloseIcon, null))), /*#__PURE__*/React.createElement(AppBar, {
    position: "static"
  }, privacyTabOnly ? /*#__PURE__*/React.createElement(Tabs, {
    TabIndicatorProps: {
      style: {
        background: "none"
      }
    },
    variant: "fullWidth",
    value: value,
    onChange: handleChange,
    classes: {
      flexContainer: classes.tabsFlexContainer
    }
  }, /*#__PURE__*/React.createElement(Tab, {
    classes: tabClasses,
    label: t("Privacy Policy")
  })) : /*#__PURE__*/React.createElement(Tabs, {
    TabIndicatorProps: {
      style: {
        background: "none"
      }
    },
    variant: "fullWidth",
    value: value,
    onChange: handleChange,
    classes: {
      flexContainer: classes.tabsFlexContainer
    }
  }, /*#__PURE__*/React.createElement(Tab, {
    classes: tabClasses,
    label: t("Terms and Conditions")
  }), /*#__PURE__*/React.createElement(Tab, {
    classes: tabClasses,
    label: t("Code of Conduct")
  }))), /*#__PURE__*/React.createElement(DialogContent, null, loading && /*#__PURE__*/React.createElement("div", {
    className: classes.loadingContainer
  }, /*#__PURE__*/React.createElement(LoadingSpinner, null)), privacyTabOnly ? renderPrivacy() : value === 0 ? renderTerms() : renderCodeOfConduct()), !privacyTabOnly && /*#__PURE__*/React.createElement(DialogActions, {
    style: {
      marginTop: 20
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    justifyContent: "center",
    spacing: 2
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 5
  }, /*#__PURE__*/React.createElement(PortalButton, {
    fullWidth: true,
    variant: "outlined",
    onClick: onClose,
    id: "termsCancelButton"
  }, t("Cancel"))), !readOnly && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(PortalButton, {
    fullWidth: true,
    buttonClasses: classes.agreeTermsButton,
    onClick: () => {
      acceptTerms();
      onClose();
    },
    disabled: loading,
    id: "termsAgreeButton"
  }, t("Agree to Terms & Conditions"))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    style: {
      marginTop: 10,
      marginBottom: 20
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true
  }, /*#__PURE__*/React.createElement(PrivacyInfo, {
    triggerPrivacyOverlay: () => setPrivacyTabOnly(true)
  })))))));
};
export default TermsDialog;
TermsDialog.propTypes = {
  backToTerms: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  showOnlyPrivacy: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  acceptTerms: PropTypes.func.isRequired,
  locale: PropTypes.string,
  readOnly: PropTypes.bool
};